// Projects list — Sales > Projects landing.
//
// Everyone lands on "Mine" (Stephanie 2026-10-16: "the default for this
// should also be mine"); "Everyone's" is one click away via the scope
// chips. Status-stage chips narrow further. Search hits the
// project name (server-side ILIKE).
//
// Each row links to /projects/:id (the Project container page from
// PR #89). A top-level "New project" button lives in the header (CEO
// 2026-07-24: "You should be able to add a new project from the Projects
// tab or the Dashboard - not just the clients tab"). It reuses the same
// NewProjectLauncherModal the Sales Dashboard uses — pick an existing
// client or create one inline, then create + navigate to the project.
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { FlashBanner } from "@/components/common/FlashBanner";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { PageHeader } from "@/components/ui/PageHeader";
import {
  FilterTabs,
  MetricStrip,
  TABLE_HEAD,
  TABLE_WRAP,
  TD,
  TH,
  TROW,
} from "@/components/ui/RecordParts";
import {
  isMeaningfulProjectState,
  projectStageLabel,
  projectStateLabel,
} from "@/lib/projectStage";
import { SalesStagePill, type OpportunitySales } from "@/components/opportunity/OpportunitySales";
import { StatusPill } from "@/components/ui/StatusPill";
import { NewProjectLauncherModal } from "@/components/projects/NewProjectLauncherModal";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { cn } from "@/lib/utils";

interface ProjectRow {
  id: string;
  projectNumber: number;
  clientId: string;
  clientName: string;
  name: string;
  statusStage: string;
  statusState: string;
  inHandsDate: string;
  propertyName: string | null;
  updatedAt: string;
}

interface ProjectsListResponse {
  rows: Array<ProjectRow & { sales?: OpportunitySales | null }>;
  nextCursor: { createdAt: string; id: string } | null;
}

type StageFilter = "all" | "opportunity_to_estimate" | "quote" | "in_production" | "shipped_invoiced" | "closed";
type ScopeFilter = "mine" | "all";

interface StageOption {
  value: StageFilter;
  label: string;
  /** Backend `stage` values to include. undefined = no filter. */
  stages?: string[];
}

const STAGE_OPTIONS: StageOption[] = [
  { value: "all", label: "All" },
  {
    value: "opportunity_to_estimate",
    label: "Pre-sale",
    stages: ["opportunity", "presentation", "estimate"],
  },
  // A signed quote is NOT in production until a PO goes to the supplier
  // (Stephanie 2026-10-13 — her quote showed under "In production" with no PO
  // sent). The project only reaches in_production when a PO is sent.
  {
    value: "quote",
    label: "Quote",
    stages: ["sales_order"],
  },
  {
    value: "in_production",
    label: "In production",
    stages: ["in_production"],
  },
  {
    value: "shipped_invoiced",
    label: "Shipped / invoiced",
    stages: ["shipped", "invoiced"],
  },
  {
    value: "closed",
    label: "Closed / lost",
    stages: ["closed", "lost"],
  },
];

function stageTone(
  s: string,
): "active" | "info" | "dormant" | "closed" | "warn" {
  switch (s) {
    case "opportunity":
    case "presentation":
    case "estimate":
      return "info";
    case "sales_order":
    case "in_production":
      return "dormant";
    case "shipped":
    case "invoiced":
      return "active";
    case "closed":
      return "closed";
    case "lost":
      return "warn";
    default:
      return "info";
  }
}

function formatDate(s: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return new Date(s + "T00:00:00").toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function timeAgo(s: string | null): string {
  if (!s) return "—";
  const then = new Date(s).getTime();
  const now = Date.now();
  const sec = Math.max(0, Math.round((now - then) / 1000));
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(s).toLocaleDateString();
}

export function ProjectsListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scope, setScope] = useState<ScopeFilter>("mine");
  const [stageGroup, setStageGroupState] = useState<StageFilter>("all");
  // "In hands within 14 days" box filters the list too (Stephanie 2026-10-14).
  const [dueSoonOnly, setDueSoonOnly] = useState(false);
  const setStageGroup = (g: StageFilter): void => {
    setDueSoonOnly(false);
    setStageGroupState(g);
  };
  const [search, setSearch] = useState("");

  // Role-gated to admin / client_rep — matches the POST /projects + POST
  // /clients guards (same gate the Sales Dashboard launcher uses).
  const canCreate = (user?.roles ?? []).some(
    (r) => r === "admin" || r === "client_rep",
  );
  const [showNewProject, setShowNewProject] = useState(false);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (search.trim()) params.set("search", search.trim());
    if (scope === "mine" && user?.sub) params.set("client_rep_id", user.sub);
    const stageOption = STAGE_OPTIONS.find((s) => s.value === stageGroup);
    // Every stage in the tab's group (the API takes ?stage= repeated). It used
    // to send only the first, so "In production" listed only quotes and
    // "Pre-sale" only opportunities (Stephanie 2026-10-13).
    for (const st of stageOption?.stages ?? []) params.append("stage", st);
    params.set("limit", "100");
    return params.toString();
  }, [scope, stageGroup, search, user]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["projects", "list", queryString],
    queryFn: () => api<ProjectsListResponse>(`/projects?${queryString}`),
  });

  const allRows = data?.rows ?? [];
  const rows = dueSoonOnly ? allRows.filter(isDueSoon) : allRows;

  // Metric strip (handoff W08): counts over the latest 100 projects in the
  // current scope, independent of the stage/search filters below.
  const summaryQuery = useQuery({
    queryKey: ["projects", "list", "summary", scope],
    queryFn: () => {
      const params = new URLSearchParams({ limit: "100" });
      if (scope === "mine" && user?.sub) params.set("client_rep_id", user.sub);
      return api<ProjectsListResponse>(`/projects?${params.toString()}`);
    },
  });
  const summaryRows = summaryQuery.data?.rows ?? null;
  const inGroup = (g: StageFilter): number | null => {
    if (summaryRows === null) return null;
    const stages = STAGE_OPTIONS.find((o) => o.value === g)?.stages ?? [];
    return summaryRows.filter((r) => stages.includes(r.statusStage)).length;
  };
  const dueSoon =
    summaryRows === null
      ? null
      : summaryRows.filter(isDueSoon).length;

  return (
    <div className="mx-auto w-full">
      <FlashBanner />
      <PageHeader
        eyebrow="Sales / Opportunities"
        title={scope === "mine" ? "My opportunities" : "All opportunities"}
        intro="One opportunity holds everything for a deal — presentations, quotes, purchase orders and invoices."
        // Role-gated (admin / client_rep) — same gate as POST /projects.
        action={canCreate && (
          <Button type="button" onClick={() => setShowNewProject(true)}>
            <Plus className="h-4 w-4" /> New opportunity
          </Button>
        )}
      />

      {!summaryQuery.isError && (
        <div className="mb-7">
          <MetricStrip
            ariaLabel="Project summary"
            cells={[
              { label: "Pre-sale", value: inGroup("opportunity_to_estimate"), note: "Presentation or estimate", onClick: () => setStageGroup("opportunity_to_estimate") },
              { label: "Quote", value: inGroup("quote"), note: "No PO sent yet", onClick: () => setStageGroup("quote") },
              { label: "In production", value: inGroup("in_production"), note: "PO sent to the supplier", onClick: () => setStageGroup("in_production") },
              {
                label: "In hands within 14 days",
                value: dueSoon,
                note: "Not shipped yet",
                attention: (dueSoon ?? 0) > 0,
                onClick: () => {
                  setStageGroupState("all");
                  setDueSoonOnly(true);
                },
              },
            ]}
          />
          {summaryQuery.data?.nextCursor && (
            <p className="mt-2 text-xs text-muted">Counts cover the latest 100 opportunities.</p>
          )}
        </div>
      )}

      {/* Stage tabs + scope + search */}
      <div className="mb-4 flex flex-wrap items-end justify-between gap-x-6 gap-y-3 border-b border-rule">
        <FilterTabs
          ariaLabel="Filter by stage"
          options={STAGE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          value={stageGroup}
          onChange={setStageGroup}
        />
        <div className="mb-2 flex w-full flex-wrap gap-2 sm:w-auto">
          <div className="inline-flex h-10 rounded-md border border-rule bg-white p-0.5" role="group" aria-label="Whose projects">
            {(
              [
                { value: "mine", label: "Mine" },
                { value: "all", label: "Everyone's" },
              ] as const
            ).map((o) => (
              <button
                key={o.value}
                type="button"
                aria-pressed={scope === o.value}
                onClick={() => setScope(o.value)}
                className={cn(
                  "rounded px-3 text-sm transition-colors",
                  scope === o.value ? "bg-ink font-medium text-white" : "text-muted hover:text-ink",
                )}
              >
                {o.label}
              </button>
            ))}
          </div>
          <div className="relative min-w-0 flex-1 sm:w-64 sm:flex-none">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden />
            <Input
              placeholder="Search opportunity name"
              aria-label="Search projects by name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
      </div>

      {dueSoonOnly && (
        <p className="mb-3 flex items-center gap-3 text-sm text-ink">
          Showing opportunities in hands within 14 days, not shipped yet.
          <button type="button" onClick={() => setDueSoonOnly(false)} className="font-semibold underline-offset-2 hover:underline">
            Show all
          </button>
        </p>
      )}

      {/* Table */}
      <div className={TABLE_WRAP} role="region" aria-label="Projects" tabIndex={0}>
        {isLoading ? (
          <div className="space-y-3 p-5" aria-label="Loading projects">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-4 w-full animate-pulse rounded bg-canvas" />
            ))}
          </div>
        ) : isError ? (
          <div className="p-5 text-sm text-risk" role="alert">
            We couldn&apos;t load opportunities. Nothing has changed — refresh to try again.
          </div>
        ) : rows.length === 0 ? (
          <div className="px-5 py-12 text-center">
            {scope === "mine" && stageGroup === "all" && !search ? (
              <>
                <p className="font-medium text-ink">You don&apos;t have any opportunities yet.</p>
                <p className="mt-1 text-sm text-muted">Use New opportunity to start one.</p>
              </>
            ) : (
              <>
                <p className="font-medium text-ink">No opportunities match.</p>
                <p className="mt-1 text-sm text-muted">Clear the search, choose All, or switch to Everyone's.</p>
              </>
            )}
          </div>
        ) : (
          <table className="w-full text-left">
            <thead className={TABLE_HEAD}>
              <tr>
                <th scope="col" className={TH}>Project</th>
                <th scope="col" className={`${TH} hidden md:table-cell`}>Account</th>
                <th scope="col" className={TH}>Stage</th>
                <th scope="col" className={`${TH} hidden sm:table-cell`}>In hands</th>
                <th scope="col" className={`${TH} hidden lg:table-cell`}>Updated</th>
              </tr>
            </thead>
            <tbody className="text-[13px]">
              {rows.map((p) => {
                const days = daysUntil(p.inHandsDate);
                const late =
                  days !== null &&
                  days < 0 &&
                  !["shipped", "invoiced", "closed", "lost"].includes(p.statusStage);
                return (
                  <tr key={p.id} className={TROW}>
                    <td className={TD}>
                      <Link
                        to={`/projects/${p.id}`}
                        className="font-medium text-ink hover:underline"
                      >
                        {p.name}
                      </Link>
                      <span className="block text-[11px] text-muted">
                        #{p.projectNumber}
                        {p.propertyName ? ` · ${p.propertyName}` : ""}
                        <span className="md:hidden"> · {p.clientName}</span>
                      </span>
                    </td>
                    <td className={`${TD} hidden md:table-cell`}>
                      <Link
                        to={`/clients/${p.clientId}`}
                        className="text-ink hover:underline"
                      >
                        {p.clientName}
                      </Link>
                    </td>
                    <td className={TD}>
                      {/* Sales stage first (CEO 2026-10-15); work progress under it. */}
                      {p.sales ? (
                        <span className="flex flex-col items-start gap-1">
                          <span className="flex items-center gap-1.5">
                            <SalesStagePill sales={p.sales} />
                            {p.sales.stale && <StatusPill tone="warn">{p.sales.stageAgeDays} days</StatusPill>}
                          </span>
                          <span className="text-xs text-muted">{projectStageLabel(p.statusStage)}</span>
                        </span>
                      ) : (
                      <StatusPill tone={stageTone(p.statusStage)}>
                        {projectStageLabel(p.statusStage)}
                      </StatusPill>
                      )}
                      {isMeaningfulProjectState(p.statusState) && (
                        <span className="ml-1.5">
                          <StatusPill tone="dormant">{projectStateLabel(p.statusState)}</StatusPill>
                        </span>
                      )}
                    </td>
                    <td className={cn(TD, "hidden sm:table-cell", late ? "text-warn" : "text-ink")}>
                      {formatDate(p.inHandsDate)}
                      {late && <span className="block text-[11px]">Past due</span>}
                    </td>
                    <td className={`${TD} hidden text-muted lg:table-cell`}>
                      {timeAgo(p.updatedAt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <p className="mt-3 text-xs text-muted">
        Showing {rows.length} {rows.length === 1 ? "opportunity" : "opportunities"}
        {data?.nextCursor && " · more available — narrow the search to find the rest"}
      </p>

      {showNewProject && (
        <NewProjectLauncherModal
          onCancel={() => setShowNewProject(false)}
          onCreated={(result) => {
            setShowNewProject(false);
            navigate(`/projects/${result.projectId}`);
          }}
        />
      )}
    </div>
  );
}

/** Not shipped/closed and in hands within 14 days — the 4th box's count and
 *  its filter use this one rule. */
function isDueSoon(r: { statusStage: string; inHandsDate: string | null }): boolean {
  if (["shipped", "invoiced", "closed", "lost"].includes(r.statusStage)) return false;
  const days = r.inHandsDate ? daysUntil(r.inHandsDate) : null;
  return days !== null && days <= 14;
}

function daysUntil(s: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(s + "T00:00:00");
  return Math.round((d.getTime() - today.getTime()) / 86_400_000);
}
